/**
 * Hex grid math for flat-top axial coordinates.
 * Based on Red Blob Games hex grid reference.
 * All functions are pure — no state, no allocation in hot paths.
 */

export interface AxialCoord {
  readonly q: number;
  readonly r: number;
}

/** Six neighbor directions for flat-top hex (E, SE, SW, W, NW, NE). */
export const HEX_DIRECTIONS: readonly AxialCoord[] = [
  { q:  1, r:  0 },
  { q:  0, r:  1 },
  { q: -1, r:  1 },
  { q: -1, r:  0 },
  { q:  0, r: -1 },
  { q:  1, r: -1 },
];

const SQRT3 = Math.sqrt(3);

/** Convert axial hex coordinate to world position (flat-top). y is always 0. */
export function axialToWorld(q: number, r: number, hexRadius: number): [x: number, z: number] {
  const x = hexRadius * 1.5 * q;
  const z = hexRadius * SQRT3 * (r + q / 2);
  return [x, z];
}

const _scratchAxial = { q: 0, r: 0 };

/** Convert world position to fractional axial, then round to nearest hex (flat-top).
 *  Returns a shared scratch object — copy values if you need to store them. */
export function worldToAxial(wx: number, wz: number, hexRadius: number): AxialCoord {
  const fq = (2 / 3) * (wx / hexRadius);
  const fr = (-1 / 3) * (wx / hexRadius) + (SQRT3 / 3) * (wz / hexRadius);

  const cx = fq;
  const cz = fr;
  const cy = -cx - cz;

  let rx = Math.round(cx);
  let ry = Math.round(cy);
  let rz = Math.round(cz);

  const dx = Math.abs(rx - cx);
  const dy = Math.abs(ry - cy);
  const dz = Math.abs(rz - cz);

  if (dx > dy && dx > dz) {
    rx = -ry - rz;
  } else if (dy > dz) {
    ry = -rx - rz;
  } else {
    rz = -rx - ry;
  }

  _scratchAxial.q = rx;
  _scratchAxial.r = rz;
  return _scratchAxial;
}

/** Get the 6 neighbors of a hex. Mutates pre-allocated entries in `out`. */
export function hexNeighbors(q: number, r: number, out: { q: number; r: number }[]): void {
  for (let i = 0; i < 6; i++) {
    out[i].q = q + HEX_DIRECTIONS[i].q;
    out[i].r = r + HEX_DIRECTIONS[i].r;
  }
}

/** Numeric key for a hex. Avoids string allocation in Set<number> lookups. */
export function axialKey(q: number, r: number): number {
  return (q + 50000) * 100001 + (r + 50000);
}

/** Axial distance between two hexes. */
export function hexDistance(aq: number, ar: number, bq: number, br: number): number {
  const dq = aq - bq;
  const dr = ar - br;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

/**
 * Generate flat-top hex corner positions for a unit hex (radius=1).
 * Returns 6 corners as [x,z] pairs. Scale by hexRadius for actual size.
 */
export function hexCorners(): Float32Array {
  const corners = new Float32Array(12); // 6 corners × 2 components
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i);
    corners[i * 2] = Math.cos(angle);
    corners[i * 2 + 1] = Math.sin(angle);
  }
  return corners;
}

/**
 * Generate an even-r rectangular grid of axial coordinates.
 * Returns flat array of [q, r] pairs.
 */
export function generateRectGrid(width: number, height: number): Int32Array {
  const result = new Int32Array(width * height * 2);
  let idx = 0;
  for (let row = 0; row < height; row++) {
    const offset = (row + (row & 1)) >> 1;
    for (let col = 0; col < width; col++) {
      result[idx++] = col - offset; // q
      result[idx++] = row;          // r
    }
  }
  return result;
}
